import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import axios from 'axios';
import { environment } from 'src/environments/environment';
import { AuthService } from '../auth/auth.service';

export interface TwoFactorStatus {
  enabled: boolean;
  hasSecret: boolean;
}

export interface TwoFactorSetupResponse {
  success: boolean;
  secret?: string;
  qrCode?: string;
  message?: string;
}

export interface TwoFactorVerifyResponse {
  success: boolean;
  message?: string;
  backupCodes?: string[];
  warning?: string;
}

@Injectable({
  providedIn: 'root'
})
export class TwoFactorAuthService {
  private readonly VERIFIED_KEY = 'meetzy_2fa_verified';
  private url = environment.server;
  
  private verifiedSubject = new BehaviorSubject<boolean>(false);
  public verified$: Observable<boolean> = this.verifiedSubject.asObservable();
  
  constructor(private authService: AuthService) {
    this.loadVerifiedState();
  }

  private async getAuthHeaders(): Promise<any> {
    const token = await this.authService.auth.currentUser?.getIdToken();
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }

  /**
   * Check if 2FA is enabled for the current admin user
   */
  async getStatus(): Promise<TwoFactorStatus> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await axios.get(`${this.url}/admin/2fa/status`, { headers });
      return {
        enabled: response.data.enabled,
        hasSecret: response.data.hasSecret
      };
    } catch (error) {
      console.error('Error getting 2FA status:', error);
      return { enabled: false, hasSecret: false };
    }
  }

  /**
   * Start 2FA setup - returns QR code and secret
   */
  async startSetup(): Promise<TwoFactorSetupResponse> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await axios.post(`${this.url}/admin/2fa/setup`, {}, { headers });
      return response.data;
    } catch (error: any) {
      console.error('Error starting 2FA setup:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to start 2FA setup'
      };
    }
  }

  /**
   * Verify setup code and enable 2FA
   */
  async verifySetup(code: string): Promise<TwoFactorVerifyResponse> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await axios.post(`${this.url}/admin/2fa/verify-setup`, { code }, { headers });
      return response.data;
    } catch (error: any) {
      console.error('Error verifying 2FA setup:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to verify code'
      };
    }
  }

  /**
   * Verify 2FA code during login
   */
  async verifyLogin(code: string): Promise<TwoFactorVerifyResponse> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await axios.post(`${this.url}/admin/2fa/verify`, { code }, { headers });
      
      if (response.data.success) {
        this.setVerified(true);
      }
      
      return response.data;
    } catch (error: any) {
      console.error('Error verifying 2FA login:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Invalid verification code'
      };
    }
  }

  /**
   * Disable 2FA
   */
  async disable(code: string): Promise<TwoFactorVerifyResponse> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await axios.post(`${this.url}/admin/2fa/disable`, { code }, { headers });
      
      if (response.data.success) {
        this.setVerified(false);
      }
      
      return response.data;
    } catch (error: any) {
      console.error('Error disabling 2FA:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to disable 2FA'
      };
    }
  }

  /**
   * Regenerate backup codes
   */
  async regenerateBackupCodes(code: string): Promise<TwoFactorVerifyResponse> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await axios.post(`${this.url}/admin/2fa/regenerate-backup-codes`, { code }, { headers });
      return response.data;
    } catch (error: any) {
      console.error('Error regenerating backup codes:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to regenerate backup codes'
      };
    }
  }

  /**
   * Check if 2FA has been verified this session
   */
  isVerified(): boolean {
    return this.verifiedSubject.value;
  }

  /**
   * Set verified state (used after successful 2FA verification)
   */
  setVerified(verified: boolean): void {
    this.verifiedSubject.next(verified);
    if (verified) {
      sessionStorage.setItem(this.VERIFIED_KEY, 'true');
    } else {
      sessionStorage.removeItem(this.VERIFIED_KEY);
    }
  }

  /**
   * Clear verification state (on logout)
   */
  clearVerification(): void {
    this.verifiedSubject.next(false);
    sessionStorage.removeItem(this.VERIFIED_KEY);
  }

  private loadVerifiedState(): void {
    const verified = sessionStorage.getItem(this.VERIFIED_KEY) === 'true';
    this.verifiedSubject.next(verified);
  }
}














